import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import TransformDate from "../../helpers/TransformDate";
import { ADMIN_LECTURE_FILE } from "../../Api/Api";
import { Axios } from "../../Api/axios";

const DATE_KEYS = new Set(["createdAt", "created_at", "updatedAt", "updated_at"]);

function formatDisplayDate(value) {
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("ar-EG", { year: "numeric", month: "short", day: "numeric" });
}

const FILE_COLUMN_META = {
  pdf:   { color: "#3b82f6", bg: "#eff6ff", icon: "📄", label: "PDF" },
  video: { color: "#8b5cf6", bg: "#f5f3ff", icon: "🎬", label: "فيديو" },
  exam:  { color: "#f59e0b", bg: "#fffbeb", icon: "📝", label: "اختبار" },
};

async function openLectureFile(filename) {
  try {
    const res = await Axios.get(ADMIN_LECTURE_FILE(filename), { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    window.open(url, "_blank", "noopener,noreferrer");
  } catch {
    alert("تعذر فتح الملف");
  }
}

function FileCell({ colKey, item }) {
  const meta = FILE_COLUMN_META[colKey];
  const fileVal = item[colKey];
  const urlVal = item[`${colKey}_url`];

  if (urlVal) {
    return (
      <a
        href={urlVal}
        target="_blank"
        rel="noopener noreferrer"
        className="file-pill"
        style={{ color: meta.color, background: meta.bg }}
      >
        <span>{meta.icon}</span> {meta.label}
      </a>
    );
  }
  if (fileVal) {
    return (
      <button
        type="button"
        className="file-pill file-pill-btn"
        style={{ color: meta.color, background: meta.bg }}
        onClick={() => openLectureFile(fileVal)}
      >
        <span>{meta.icon}</span> {meta.label}
      </button>
    );
  }
  return <span style={{ color: "#d1d5db" }}>—</span>;
}

function SkeletonRows({ cols }) {
  return Array.from({ length: 4 }).map((_, i) => (
    <tr key={i} className="skeleton-row">
      {Array.from({ length: cols + 2 }).map((_, j) => (
        <td key={j}>
          <div className="skeleton-cell" style={{ width: j === 0 ? "40px" : "80%" }} />
        </td>
      ))}
    </tr>
  ));
}

export default function TableShow(props) {
  const safeData = Array.isArray(props.data) ? props.data : [];
  const currentUser = props.currentUser || { name: "" };
  const [search, setSearch] = useState("");
  const [date, setDate] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const filteredByDate =
    date.length !== 0
      ? safeData.filter((item) => TransformDate(item.created_at ?? item.createdAt) === date)
      : safeData;

  const searchFilteredByDate =
    date.length !== 0
      ? filteredData.filter((item) => TransformDate(item.created_at ?? item.createdAt) === date)
      : filteredData;

  const displayData = search.length > 0 ? searchFilteredByDate : filteredByDate;

  async function getSearchedData() {
    try {
      const res = await Axios.post(`${props.searchLink}/search?title=${search}`);
      const result = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data.data)
        ? res.data.data
        : [];
      setFilteredData(result);
    } catch {
      setFilteredData([]);
    } finally {
      setSearchLoading(false);
    }
  }

  useEffect(() => {
    const debounce = setTimeout(() => {
      search.length > 0 ? getSearchedData() : setSearchLoading(false);
    }, 500);
    return () => clearTimeout(debounce);
  }, [search]);

  const isLoading = props.loading || searchLoading;

  return (
    <div className="table-card">
      {/* Filters */}
      <div className="table-filters">
        <div className="filter-input-wrap">
          <svg className="filter-icon" width="15" height="15" fill="currentColor" viewBox="0 0 24 24">
            <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
          <input
            className="filter-input"
            type="search"
            placeholder="ابحث هنا..."
            onChange={(e) => {
              setSearch(e.target.value);
              setSearchLoading(true);
            }}
          />
        </div>

        <input
          className="date-input"
          type="date"
          onChange={(e) => setDate(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="table-scroll">
        <table className="dash-table">
          <thead>
            <tr>
              <th>#</th>
              {props.header.map((col, i) => (
                <th key={i}>{col.name}</th>
              ))}
              <th>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows cols={props.header.length} />
            ) : displayData.length === 0 ? (
              <tr>
                <td colSpan={props.header.length + 2}>
                  <div className="table-state">
                    <svg width="56" height="56" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 6h-2.18c.07-.44.18-.86.18-1.3C18 2.57 15.86.6 13.22.6c-1.6 0-3 .8-3.87 2.02L8 4.38 6.65 2.62C5.78 1.4 4.38.6 2.78.6.14.6-2 2.57-2 4.7c0 .44.11.86.18 1.3H-2c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h22c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM4 19H2v-2h2v2zm0-4H2v-2h2v2zm4 4H6v-2h2v2zm0-4H6v-2h2v2zm4 4h-2v-2h2v2zm0-4h-2v-2h2v2zm4 4h-2v-2h2v2zm0-4h-2v-2h2v2zm4 4h-2v-2h2v2zm0-4h-2v-2h2v2z"/>
                    </svg>
                    <p>لا توجد بيانات متاحة حالياً</p>
                    <small style={{ color: "#d1d5db", fontSize: "0.8rem" }}>سيتم عرض البيانات هنا بمجرد إضافتها</small>
                  </div>
                </td>
              </tr>
            ) : (
              displayData.map((item, key) => (
                <tr key={item.id ?? key}>
                  <td><span className="id-badge">{item.id}</span></td>

                  {props.header.map((col, colKey) => (
                    <td key={colKey}>
                      {col.key === "image" ? (
                        <img className="row-thumb" src={item[col.key]} alt="" />
                      ) : col.key === "images" ? (
                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                          {item[col.key]?.map((img, idx) => (
                            <img key={idx} className="row-thumb" src={img.image} alt="" />
                          ))}
                        </div>
                      ) : col.key === "teacher_photo" ? (
                        item[col.key]
                          ? <img className="row-thumb" src={item[col.key]} alt="" />
                          : <span style={{ color: "#9ca3af", fontSize: "0.8rem" }}>—</span>
                      ) : FILE_COLUMN_META[col.key] ? (
                        <FileCell colKey={col.key} item={item} />
                      ) : DATE_KEYS.has(col.key) ? (
                        formatDisplayDate(item[col.key]) || <span style={{ color: "#d1d5db" }}>—</span>
                      ) : (
                        <span>
                          {item[col.key] || <span style={{ color: "#d1d5db" }}>—</span>}
                          {currentUser && item[col.key] === currentUser.name && (
                            <span style={{ color: "#00a74f", fontSize: "0.75rem", marginRight: "4px" }}>(أنت)</span>
                          )}
                        </span>
                      )}
                    </td>
                  ))}

                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Link to={`${item.id}`} className="action-btn action-edit" title="تعديل">
                        <FontAwesomeIcon icon={faEdit} />
                      </Link>
                      {currentUser.name !== item.name && (
                        <button
                          className="action-btn action-delete"
                          onClick={() => props.delete(item.id)}
                          title="حذف"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
